import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Container, Alert, InputLabel, Button, IconButton } from '@mui/material';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { ICustomerLight, ICustomerMassClosedThroughUpdateRequest } from "../../../types/customer";

import { AppConfig } from "../../../config/appConfig";
import { ErrorProvider } from "../../../classes/errorProvider";
import { useUserContext } from "../../../contexts/userContext";

import { CustomerService } from "../../../services/customerService";

import CustomerClosingTable from "../../tables/customerClosingTable";
import Navigation from "../../../components/header";
import PermissionGate from "../../../components/permissionGate";
import Spinner from "../../../components/spinner";
import WorkingModal, { IWorkingModalProps } from "../../workingModal";
import Json from "../../json";

import "../../../styles/entity/customerClosing.scss";
import { Icons } from "../../icons";

const CustomerClosing = () => {
	const userContext = useUserContext();
	const [gridView, setGridView] = useState<ICustomerLight[]>();
	const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
	const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
	const [newClosedThroughDate, setNewClosedThroughDate] = useState<dayjs.Dayjs>(dayjs().startOf('day'));
	const [selectedCustomerIds, setSelectedCustomerIds] = useState<number[]>([]);
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);

	const loadCustomerData = async () => {
		setIsLoadingCustomers(true);

		CustomerService.getActiveCustomers()
			.then((response) => {
				console.log('CustomerService.getCustomers: SUCCESS', response);
				let customers = CustomerService.mapCustomerLightList(response.data);
				setGridView(customers);
			})
			.catch((error) => {
				console.log('CustomerService.getCustomers: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
				else {
					setErrorMessage(ErrorProvider.getError(error));
				}
			})
			.finally(() => {
				setIsLoadingCustomers(false);
			});
	}

	const closeCustomers = () => {
		setErrorMessage(undefined);
		setSuccessMessage(undefined);
		setWorkingModalParams({ params: { content: `Updating ${selectedCustomerIds.length} customer${selectedCustomerIds.length !== 1 ? 's' : ''}...` } });

		const request = {
			customerIds: selectedCustomerIds,
			closedThroughDate: newClosedThroughDate.toDate()
		} as ICustomerMassClosedThroughUpdateRequest;

		CustomerService.massUpdateClosedThroughDate(request)
			.then((response) => {
				loadCustomerData();
				console.log('CustomerService.massUpdateClosedThroughDate: SUCCESS', response);
				setSuccessMessage(`Successfully updated ${selectedCustomerIds.length} customers.`);
			})
			.catch((error) => {
				console.log('CustomerService.massUpdateClosedThroughDate: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
				else {
					setErrorMessage(ErrorProvider.getError(error));
				}
			})
			.finally(() => {
				setWorkingModalParams(undefined);
				window.scrollTo({ top: 0, behavior: 'smooth' });
			});
	}

	const handleCustomerSelectionChange = (selectedIds: number[]) => {
		setSelectedCustomerIds(selectedIds ?? []);
	}

	useEffect(() => {
		loadCustomerData();
	}, [])

	return (
		<div className="hwi-customer-closing">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canChangeClosedThroughDate}>
					<section className="hwi-entity-search-bar">
						<div className="search-header">
							<div className="caption">
								<NavLink to="/customer">
									<Icons.GoBack fill="#9399ad" height="24px"></Icons.GoBack>
								</NavLink>
								<div className="title">Close Customers</div>
							</div>
							<div className="links">
							</div>
						</div>
					</section>
					{errorMessage &&
						<Alert className="alert-error"
							severity="error"
							action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
							{errorMessage}
						</Alert>
					}
					{successMessage &&
						<Alert className="alert-success"
							severity="success"
							action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
							{successMessage}
						</Alert>
					}
					<section className="instructions">
						<p>
							Select a new closed-through date along with all the customers it should be applied to, then click <strong>Close Selected Customers</strong> at the bottom of the page.
						</p>
					</section>
					<section className="controls">
						<div className="row">
							<InputLabel>New Closed-Through Date</InputLabel>
							<div className="closed-through-date-field">
								<LocalizationProvider dateAdapter={AdapterDayjs}>
									<DatePicker className="start-date-field" value={newClosedThroughDate} format='MM/DD/YYYY' onChange={(value) => setNewClosedThroughDate(value?.startOf('day'))} />
								</LocalizationProvider>
							</div>
						</div>
					</section>
					<section className="results">
						<Spinner dependency={isLoadingCustomers} text="Loading active customers...">
							<section className="results">
								<CustomerClosingTable data={gridView} onCustomerSelectionChange={handleCustomerSelectionChange}></CustomerClosingTable>
							</section>
						</Spinner>
					</section>
					<section className="actions">
						<NavLink to="/customer"><Button variant="contained" color="error" size="large" onClick={closeCustomers}>Cancel</Button></NavLink>
						<Button variant="contained" color="primary" size="large" disabled={!newClosedThroughDate || selectedCustomerIds.length == 0} onClick={closeCustomers}>Close Selected Customers</Button>
					</section>
				</PermissionGate>
			</Container>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
			<Json data={errorMessage}></Json>
		</div>
	);
};

export default CustomerClosing;